import { useState, useCallback, useRef, useEffect } from "react";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { useMode } from "@/providers/ModeProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { CreatePromiseFormCustomer } from "./useCreatePromiseData";

// --- GraphQL types for Customer list ---

interface CustomerGraphQLNode {
	customerId: number;
	customerName: string | null;
	accountNumber: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	creditControllerId: number | null;
}

interface GraphQLConnection<T> {
	edges: Array<{ node: T }>;
	pageInfo: {
		endCursor: string | null;
		hasNextPage: boolean;
	};
}

interface CustomerListGraphQLResponse {
	customers: {
		customerList: GraphQLConnection<CustomerGraphQLNode>;
	};
}

const CUSTOMER_LIST_QUERY = `
  query CustomerList($first: Int, $after: String, $sort: [CustomerSortSortByInput!], $filter: CustomerFilterInput) {
    customers {
      customerList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            customerId
            customerName
            accountNumber
            contactEmail
            contactPhone
            creditControllerId
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const CUSTOMER_SEARCH_PAGE_SIZE = 20;

function mapGraphQLNodeToFormCustomer(
	node: CustomerGraphQLNode,
): CreatePromiseFormCustomer {
	return {
		id: String(node.customerId),
		name: node.customerName ?? "",
		accountNumber: node.accountNumber ?? "",
		email: node.contactEmail ?? "",
		phone: node.contactPhone ?? "",
		creditControllerId: node.creditControllerId != null
			? String(node.creditControllerId)
			: undefined,
	};
}

// --- Mock data for demo mode ---

const MOCK_CUSTOMERS: CreatePromiseFormCustomer[] = [
	{
		id: "1",
		name: "Acme Corporation",
		accountNumber: "ACC-001",
		email: "accounts@acme.com",
		phone: "+1 (555) 123-4567",
	},
	{
		id: "2",
		name: "Global Industries Ltd",
		accountNumber: "ACC-002",
		email: "finance@globalind.com",
		phone: "+1 (555) 234-5678",
	},
	{
		id: "3",
		name: "Tech Solutions Inc",
		accountNumber: "ACC-003",
		email: "billing@techsol.com",
		phone: "+1 (555) 345-6789",
	},
];

function filterMockCustomers(
	customers: CreatePromiseFormCustomer[],
	search: string,
): CreatePromiseFormCustomer[] {
	if (!search.trim()) return customers;
	const lower = search.toLowerCase();
	return customers.filter(
		(x) =>
			x.name.toLowerCase().includes(lower) ||
			x.accountNumber.toLowerCase().includes(lower),
	);
}

// --- Hook ---

export interface CustomerSearchGraphQLResult {
	customers: CreatePromiseFormCustomer[];
	isLoading: boolean;
	isFetchingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	reset: () => void;
}

export function useCustomerSearchGraphQL(
	search: string,
): CustomerSearchGraphQLResult {
	const apiConfig = useApiConfig();
	const { isDemo } = useMode();
	const [customers, setCustomers] = useState<CreatePromiseFormCustomer[]>([]);
	const [hasMore, setHasMore] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	const lastSearchRef = useRef(search);
	const endCursorRef = useRef<string | null>(null);

	const fetchCustomers = useCallback(
		async (searchValue: string, afterCursor: string | null, append: boolean) => {
			if (isDemo) {
				const filtered = filterMockCustomers(MOCK_CUSTOMERS, searchValue);
				setCustomers(filtered);
				setHasMore(false);
				setIsLoading(false);
				setIsFetchingMore(false);
				return;
			}

			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			try {
				const variables: Record<string, unknown> = {
					first: CUSTOMER_SEARCH_PAGE_SIZE,
					sort: [{ field: "CustomerName", order: "Ascending" }],
				};

				if (afterCursor) {
					variables.after = afterCursor;
				}

				if (searchValue.trim()) {
					variables.filter = { summary: searchValue };
				}

				const response = await graphqlQuery<CustomerListGraphQLResponse>(
					apiConfig,
					CUSTOMER_LIST_QUERY,
					variables,
					controller.signal,
				);

				const connection = response.customers.customerList;
				const mapped = connection.edges.map((e) =>
					mapGraphQLNodeToFormCustomer(e.node),
				);

				if (append) {
					setCustomers((prev) => [...prev, ...mapped]);
				} else {
					setCustomers(mapped);
				}

				endCursorRef.current = connection.pageInfo.endCursor;
				setHasMore(connection.pageInfo.hasNextPage);
			} catch (error: unknown) {
				if ((error as Error)?.name === "AbortError") return;
				if (!append) {
					setCustomers([]);
				}
				setHasMore(false);
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false);
					setIsFetchingMore(false);
				}
			}
		},
		[apiConfig, isDemo],
	);

	useEffect(() => {
		lastSearchRef.current = search;
		endCursorRef.current = null;
		setIsLoading(true);
		setIsFetchingMore(false);
		fetchCustomers(search, null, false);
	}, [search, fetchCustomers]);

	const loadMore = useCallback(() => {
		if (isFetchingMore || !hasMore) return;
		setIsFetchingMore(true);
		fetchCustomers(lastSearchRef.current, endCursorRef.current, true);
	}, [isFetchingMore, hasMore, fetchCustomers]);

	const reset = useCallback(() => {
		abortControllerRef.current?.abort();
		lastSearchRef.current = "";
		endCursorRef.current = null;
		setCustomers([]);
		setHasMore(false);
		setIsLoading(false);
		setIsFetchingMore(false);
	}, []);

	return { customers, isLoading, isFetchingMore, hasMore, loadMore, reset };
}
